import { useCallback, useEffect, useState } from "react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Download } from "lucide-react";
import { adminGet } from "@/lib/adminApi";

// ---------- Sélecteur de période ----------
const PERIODS = [
  ["today", "Aujourd'hui"], ["yesterday", "Hier"], ["7d", "7 derniers jours"], ["week", "Semaine en cours"],
  ["30d", "30 derniers jours"], ["month", "Mois en cours"], ["3m", "3 mois"], ["6m", "6 mois"],
  ["year", "Année en cours"], ["all", "Depuis le début"], ["custom", "Période personnalisée"],
];

function periodRange(key, customFrom, customTo) {
  const iso = (d) => d.toISOString().slice(0, 10);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (key) {
    case "today": return [iso(today), iso(today)];
    case "yesterday": { const y = new Date(today); y.setDate(y.getDate() - 1); return [iso(y), iso(y)]; }
    case "7d": { const d = new Date(today); d.setDate(d.getDate() - 6); return [iso(d), iso(today)]; }
    case "week": { const d = new Date(today); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return [iso(d), iso(today)]; }
    case "30d": { const d = new Date(today); d.setDate(d.getDate() - 29); return [iso(d), iso(today)]; }
    case "month": return [iso(new Date(today.getFullYear(), today.getMonth(), 1)), iso(today)];
    case "3m": { const d = new Date(today); d.setMonth(d.getMonth() - 3); return [iso(d), iso(today)]; }
    case "6m": { const d = new Date(today); d.setMonth(d.getMonth() - 6); return [iso(d), iso(today)]; }
    case "year": return [iso(new Date(today.getFullYear(), 0, 1)), iso(today)];
    case "custom": return [customFrom || null, customTo || null];
    default: return ["2026-01-01", iso(today)];
  }
}

const KIND_LABELS = { abonnement: "Abonnements RAV+", subscription: "Abonnements RAV+", programme: "Programmes", visio: "Visios", cadeau: "Cartes cadeaux", gift: "Cartes cadeaux", autre: "Autres" };
const fmtDay = (iso) => iso.slice(8, 10) + "/" + iso.slice(5, 7);

function ChartCard({ title, testid, children }) {
  return (
    <div className="rav-admin-form" data-testid={testid} style={{ marginTop: 16 }}>
      <h3 className="rav-admin-h3" style={{ marginTop: 0 }}>{title}</h3>
      <div style={{ width: "100%", height: 260 }}>{children}</div>
    </div>
  );
}

export default function Analytics() {
  const [period, setPeriod] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    const [from, to] = periodRange(period, customFrom, customTo);
    adminGet(`/analytics?date_from=${from}&date_to=${to}`).then(setData).catch(() => {});
  }, [period, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data) return;
    const lines = ["Date;Visites;Visiteurs uniques;Connexions;Inscriptions;Utilisatrices actives;Instants créés;CA (€)"];
    data.days.forEach((d) => lines.push([d.date, d.visits, d.unique_visitors, d.logins, d.signups, d.active_users, d.instants, String(d.ca).replace(".", ",")].join(";")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "analytique-rav.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!data) return <p className="rav-page-sub" data-testid="analytics-loading">Chargement des statistiques…</p>;
  const t = data.totals;
  const chartData = data.days.map((d) => ({ ...d, jour: fmtDay(d.date) }));

  const kpis = [
    ["Visites", t.visits, "kpi-visits"],
    ["Visiteurs uniques", t.unique_visitors, "kpi-uniques"],
    ["Connexions", t.logins, "kpi-logins"],
    ["Nouvelles inscrites", t.signups, "kpi-signups"],
    ["Visiteur → inscription", `${t.conversion} %`, "kpi-conversion"],
    ["Utilisatrices actives", t.active_users_total, "kpi-actives"],
    ["Instants créés", t.instants, "kpi-instants"],
    ["Abonnées RAV+ (total)", t.premium_active, "kpi-premium"],
    ["Comptes clientes (total)", t.users_total, "kpi-users"],
    ["Chiffre d'affaires", `${t.ca.toFixed(2)} €`, "kpi-ca"],
  ];

  return (
    <div data-testid="admin-analytics">
      <h2 className="rav-admin-h2">Analytique — trafic réel de l'application</h2>
      <p className="rav-page-sub">
        Du {data.date_from.split("-").reverse().join("/")} au {data.date_to.split("-").reverse().join("/")} · mesures anonymes et réelles de votre application.
      </p>

      <div className="rav-partners-period" data-testid="analytics-period-picker">
        <select className="rav-input" data-testid="analytics-period" value={period} onChange={(e) => setPeriod(e.target.value)}>
          {PERIODS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        {period === "custom" && (
          <>
            <input className="rav-input" data-testid="analytics-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            <input className="rav-input" data-testid="analytics-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </>
        )}
        <button type="button" className="rav-secondary-button rav-session-btn" data-testid="analytics-export" onClick={exportCsv}>
          <Download size={14} strokeWidth={1.6} /> Exporter (CSV)
        </button>
      </div>

      <div className="rav-admin-grid" data-testid="analytics-kpis">
        {kpis.map(([label, value, id]) => (
          <div key={id} className="rav-admin-card" data-testid={id}>
            <div className="rav-admin-card-value">{value}</div>
            <div className="rav-admin-card-label">{label}</div>
          </div>
        ))}
      </div>

      <ChartCard title="Visites et visiteurs uniques par jour" testid="chart-visites">
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8dccf" />
            <XAxis dataKey="jour" tick={{ fontSize: 11, fill: "#82624a" }} interval="preserveStartEnd" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#82624a" }} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="visits" name="Visites" stroke="#b98a2f" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="unique_visitors" name="Visiteurs uniques" stroke="#5f7a61" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Connexions et inscriptions par jour" testid="chart-connexions">
        <ResponsiveContainer>
          <BarChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8dccf" />
            <XAxis dataKey="jour" tick={{ fontSize: 11, fill: "#82624a" }} interval="preserveStartEnd" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#82624a" }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="logins" name="Connexions" fill="#4a6a7a" radius={[4, 4, 0, 0]} />
            <Bar dataKey="signups" name="Inscriptions" fill="#3e7c4f" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Utilisatrices actives et Instants créés par jour" testid="chart-activite">
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8dccf" />
            <XAxis dataKey="jour" tick={{ fontSize: 11, fill: "#82624a" }} interval="preserveStartEnd" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#82624a" }} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="active_users" name="Utilisatrices actives" stroke="#6a5f8a" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="instants" name="Instants créés" stroke="#c4705a" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Chiffre d'affaires par jour (€)" testid="chart-ca">
        <ResponsiveContainer>
          <BarChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8dccf" />
            <XAxis dataKey="jour" tick={{ fontSize: 11, fill: "#82624a" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: "#82624a" }} />
            <Tooltip formatter={(v) => `${Number(v).toFixed(2)} €`} />
            <Bar dataKey="ca" name="CA (€)" fill="#b98a2f" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {Object.keys(data.ca_by_kind).length > 0 && (
        <>
          <h3 className="rav-admin-h3">Chiffre d'affaires par nature (période)</h3>
          <table className="rav-partners-table" data-testid="analytics-ca-kind">
            <thead><tr><th>Nature du revenu</th><th>Montant</th></tr></thead>
            <tbody>
              {Object.entries(data.ca_by_kind).map(([kind, amount], i) => (
                <tr key={kind} data-testid={`ca-kind-${i}`}><td>{KIND_LABELS[kind] || kind}</td><td>{Number(amount).toFixed(2)} €</td></tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h3 className="rav-admin-h3">Détail jour par jour</h3>
      <div style={{ overflowX: "auto" }}>
        <table className="rav-partners-table" data-testid="analytics-table">
          <thead>
            <tr><th>Jour</th><th>Visites</th><th>Visiteurs uniques</th><th>Connexions</th><th>Inscriptions</th><th>Actives</th><th>Instants</th><th>CA (€)</th></tr>
          </thead>
          <tbody>
            {[...data.days].reverse().map((d, i) => (
              <tr key={d.date} data-testid={`analytics-row-${i}`}>
                <td>{d.date.split("-").reverse().join("/")}</td>
                <td>{d.visits}</td><td>{d.unique_visitors}</td><td>{d.logins}</td>
                <td>{d.signups}</td><td>{d.active_users}</td><td>{d.instants}</td><td>{d.ca.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="rav-page-sub" style={{ marginTop: 10 }}>
        Les visites et connexions sont comptées depuis la mise en place de la mesure (anonyme, conforme RGPD). Les inscriptions, l'activité et le chiffre d'affaires remontent à la création de l'application.
      </p>
    </div>
  );
}
